import { NIVEIS } from './niveis.js';
/**
 * A matriz RBAC — transcrição literal de `tb-rbac` em #v-acessos.
 *
 * Esta é a FONTE ÚNICA. O backend a usa para negar (guard, escopo,
 * projeção); o frontend a usa para esconder item de menu, exatamente
 * como o atributo `data-roles` faz na sidebar do protótipo.
 *
 *   >>> A cópia do frontend é COSMÉTICA. <<<
 *   A decisão real é sempre do servidor. Nunca confie nesta matriz
 *   no navegador para proteger dado.
 *
 * Em produção ela é apenas o CATÁLOGO PADRÃO semeado numa arena nova:
 * a tabela `permissoes` é a autoridade em tempo de execução, e a tela
 * de #v-acessos pode alterá-la (documento 19). Isso mantém aberta a
 * pergunta "perfis são globais ou por arena?" sem custo agora.
 */
export const MATRIZ = {
    dashboard: {
        dono: 'total',
        gestao: 'sem_margem_custos',
        professor: 'nao',
        aluno: 'nao',
    },
    alunos: {
        dono: 'total',
        gestao: 'criar_editar',
        professor: 'proprias_turmas',
        aluno: 'proprios_dados',
    },
    perfis: {
        dono: 'total',
        gestao: 'nao',
        professor: 'nao',
        aluno: 'nao',
    },
    turmas: {
        dono: 'total',
        gestao: 'criar_editar',
        professor: 'propria_grade_chamada',
        aluno: 'propria_turma',
    },
    planos: {
        dono: 'total',
        gestao: 'ver_aplicar',
        professor: 'nao',
        aluno: 'proprio_plano',
    },
    cobrancas: {
        dono: 'total',
        gestao: 'registrar_pagamento',
        professor: 'nao',
        aluno: 'propria_fatura',
    },
    agenda: {
        dono: 'total',
        gestao: 'criar_editar_cancelar',
        professor: 'visualizar',
        aluno: 'horarios_livres_solicitacao',
    },
    bi: {
        dono: 'total',
        gestao: 'total',
        professor: 'nao',
        aluno: 'nao',
    },
    financeiro: {
        dono: 'total',
        gestao: 'lancar_sem_folha',
        professor: 'nao',
        aluno: 'nao',
    },
    folha: {
        dono: 'total',
        gestao: 'nao',
        professor: 'proprio_pagamento',
        aluno: 'nao',
    },
    estoque: {
        dono: 'total',
        // ─────────────────────────────────────────────────────────────
        // DIVERGÊNCIA NO PROTÓTIPO — precisa de decisão do dono.
        //
        // A linha `['Estoque','Total','Total','Não','Não']` de tb-rbac diz
        // que a Gestão tem acesso TOTAL ao estoque, incluindo custo e
        // margem. Mas dois outros pontos do mesmo protótipo dizem o
        // contrário:
        //   · a tela de login descreve Camila como
        //     "Sem margens, custos e folha de professores";
        //   · a linha de Dashboard dá a ela "Sem margem e custos".
        //
        // Transcrito FIELMENTE à matriz (que é a tela de permissões e
        // portanto a autoridade). Para restringir, troque para
        // 'sem_margem_custos' — o nível já existe em niveis.ts e a
        // projeção correspondente já está prevista em 13-estoque-comandas.md.
        // ─────────────────────────────────────────────────────────────
        gestao: 'total',
        professor: 'nao',
        aluno: 'nao',
    },
    comanda: {
        dono: 'total',
        gestao: 'abrir_lancar_fechar',
        professor: 'abrir_lancar',
        aluno: 'propria_comanda',
    },
};
/** Rótulo de cada recurso na matriz de #v-acessos. */
export const ROTULO_RECURSO = {
    dashboard: 'Dashboard executivo',
    alunos: 'Cadastro de alunos',
    perfis: 'Perfis e permissões',
    turmas: 'Turmas',
    planos: 'Planos e valores',
    cobrancas: 'Cobranças e inadimplência',
    agenda: 'Agenda e reservas',
    bi: 'BI de ocupação',
    financeiro: 'Financeiro',
    folha: 'Folha de professores',
    estoque: 'Estoque',
    comanda: 'Comanda',
};
export const ROTULO_PERFIL = {
    dono: 'Dono',
    gestao: 'Gestão e recepção',
    professor: 'Professor',
    aluno: 'Aluno',
};
/**
 * Resolve a célula (perfil, recurso) para o nível concreto.
 *
 * Lança quando a matriz aponta para um nível que o recurso não oferece
 * — erro de configuração que deve estourar no boot, não silenciar.
 */
export function nivelDe(perfil, recurso) {
    const chave = MATRIZ[recurso][perfil];
    const nivel = NIVEIS[recurso][chave];
    if (!nivel) {
        throw new Error(`Matriz inconsistente: o recurso "${recurso}" não oferece o nível "${chave}" ` +
            `atribuído ao perfil "${perfil}". Níveis válidos: ${Object.keys(NIVEIS[recurso]).join(', ')}.`);
    }
    return nivel;
}
//# sourceMappingURL=matriz.js.map